<script setup lang="ts">
import { computed, useTemplateRef } from 'vue';
import InputNumber from 'primevue/inputnumber';
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';
import Popover from 'primevue/popover';
import Button from 'primevue/button';
import { useWeightUnit } from 'shared/common/composables/fieldUnit';
import {
  weightUnits,
  weightUnitSymbol,
  weightUnitFractionDigits,
  toDisplayWeight,
  fromDisplayWeight,
  type WeightUnit,
} from 'shared/common/domain/weightUnits';

const props = defineProps<{
  /** Value in kilograms. */
  modelValue: number | null;
  /** Stable key used to persist this field's chosen unit. */
  unitKey: string;
  /** Optional minimum, in kilograms. */
  min?: number;
  /** Id applied to the inner input, for label association. */
  inputId?: string;
}>();

const emit = defineEmits<{ 'update:modelValue': [number | null] }>();

const unit = useWeightUnit(props.unitKey);

const displayValue = computed<number | null>({
  get: () =>
    props.modelValue === null || props.modelValue === undefined
      ? null
      : toDisplayWeight(props.modelValue, unit.value),
  set: (value) =>
    emit('update:modelValue', value === null || value === undefined ? null : fromDisplayWeight(value, unit.value)),
});

const minDisplay = computed(() =>
  props.min === undefined ? undefined : toDisplayWeight(props.min, unit.value),
);

const popover = useTemplateRef('popover');

function toggle(event: Event) {
  popover.value?.toggle(event);
}

function select(next: WeightUnit) {
  unit.value = next;
  popover.value?.hide();
}
</script>

<template>
  <InputGroup>
    <InputNumber
      pt:pcInputText:root:autocomplete="off"
      v-model:modelValue="displayValue"
      :min="minDisplay"
      :minFractionDigits="0"
      :maxFractionDigits="weightUnitFractionDigits(unit)"
      :inputId="inputId"
    ></InputNumber>
    <InputGroupAddon class="unit-addon" @click="toggle" role="button" tabindex="0">
      {{ weightUnitSymbol(unit) }}
      <i class="pi pi-angle-down unit-chevron"></i>
    </InputGroupAddon>
  </InputGroup>
  <Popover ref="popover">
    <div class="unit-list">
      <Button
        v-for="u in weightUnits"
        :key="u"
        text
        size="small"
        :label="weightUnitSymbol(u)"
        :severity="u === unit ? 'primary' : 'secondary'"
        @click="select(u)"
      ></Button>
    </div>
  </Popover>
</template>

<style scoped>
.unit-addon {
  cursor: pointer;
  user-select: none;
  gap: .2rem;
}

.unit-chevron {
  font-size: .7rem;
  opacity: .7;
}

.unit-list {
  display: flex;
  flex-direction: column;
  min-width: 3rem;
}
</style>
